import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RegisterAvatar, IdentityCheck, IdentityCheckSummary } from '@/features/photos/RegisterIdentity'
import { trainerApi, type AttendanceStatus, type AttendanceOutcome, type RegisterAttendeeDto, type StaffSearchResultDto } from '@/api/trainer'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CourseMaterialsSection } from './CourseMaterialsSection'
import {
  ChevronLeft, MapPin, Users, CheckCircle2, XCircle, Clock,
  UserPlus, Trash2, Search, Plus, Phone, Mail, BookOpen,
  ExternalLink, Ban, AlertTriangle, Globe,
} from 'lucide-react'
import { useTrainerDates } from '@/hooks/useDates'
import { LiveRoomCard } from '@/features/live/LiveRoomCard'
import { SignInPanel } from '@/features/attendance/SignInPanel'
import { useLiveStatus } from '@/features/live/useLiveStatus'
import { PageTabs, TabCount, TabLiveDot, TabPanel, usePageTab } from '@/components/shared/PageTabs'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mapsUrl(address: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

const ATTENDANCE_CYCLE: AttendanceStatus[] = ['NotRecorded', 'Present', 'Absent']

function nextStatus(current: AttendanceStatus): AttendanceStatus {
  const idx = ATTENDANCE_CYCLE.indexOf(current)
  return ATTENDANCE_CYCLE[(idx + 1) % ATTENDANCE_CYCLE.length]
}

type AttendanceBadgeProps = { status: AttendanceStatus; disabled?: boolean; onClick?: () => void }

function AttendanceBadge({ status, disabled, onClick }: AttendanceBadgeProps) {
  const styles: Record<AttendanceStatus, string> = {
    Present:     'bg-green-100 text-green-700 border-green-300 hover:bg-green-200',
    Absent:      'bg-red-50 text-red-600 border-red-300 hover:bg-red-100',
    NotRecorded: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200',
  }
  const icons: Record<AttendanceStatus, React.ReactNode> = {
    Present:     <CheckCircle2 size={13} />,
    Absent:      <XCircle size={13} />,
    NotRecorded: <Clock size={13} />,
  }
  const labels: Record<AttendanceStatus, string> = {
    Present: 'Present', Absent: 'Absent', NotRecorded: 'Not recorded',
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${styles[status]} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {icons[status]}
      {labels[status]}
    </button>
  )
}

type OutcomeToggleProps = { outcome: AttendanceOutcome; disabled?: boolean; onClick?: () => void }

function OutcomeToggle({ outcome, disabled, onClick }: OutcomeToggleProps) {
  const style = outcome === 'Failed'
    ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
    : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title="Click to toggle pass/fail"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${style} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {outcome === 'Failed' ? <Ban size={13} /> : <CheckCircle2 size={13} />}
      {outcome === 'Failed' ? 'Failed' : 'Passed'}
    </button>
  )
}

// ─── Walk-in modal ────────────────────────────────────────────────────────────

function WalkInModal({
  bookingId,
  onClose,
  onAdded,
}: {
  bookingId: string
  onClose: () => void
  onAdded: (attendee: RegisterAttendeeDto) => void
}) {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'search' | 'new'>('search')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [lastAdded, setLastAdded] = useState<string | null>(null)

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setDebouncedQ(q), 300)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [q])

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['staff-search', bookingId, debouncedQ],
    queryFn: () => trainerApi.searchClientStaff(bookingId, debouncedQ).then(r => r.data),
    enabled: tab === 'search',
  })

  const addExistingMutation = useMutation({
    mutationFn: (staffMemberId: string) =>
      trainerApi.addWalkIn(bookingId, { existingStaffMemberId: staffMemberId }).then(r => r.data),
    onSuccess: (attendee) => {
      onAdded(attendee)
      setQ('')
      queryClient.invalidateQueries({ queryKey: ['staff-search', bookingId] })
      setLastAdded(`${attendee.firstName} ${attendee.lastName}`)
      setTimeout(() => setLastAdded(null), 2500)
    },
  })

  const addNewMutation = useMutation({
    mutationFn: () =>
      trainerApi.addWalkIn(bookingId, {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
      }).then(r => r.data),
    onSuccess: (attendee) => {
      onAdded(attendee)
      setFirstName(''); setLastName(''); setEmail('')
      queryClient.invalidateQueries({ queryKey: ['staff-search', bookingId] })
      setLastAdded(`${attendee.firstName} ${attendee.lastName}`)
      setTimeout(() => setLastAdded(null), 2500)
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <UserPlus size={16} className="text-indigo-500" /> Add walk-in
          </h2>
          <div className="flex items-center gap-3">
            {lastAdded && (
              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                <CheckCircle2 size={13} /> {lastAdded} added
              </span>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none cursor-pointer">×</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {(['search', 'new'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2.5 text-sm font-medium cursor-pointer transition-colors ${tab === t ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t === 'search' ? 'Search existing staff' : 'Add new person'}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === 'search' ? (
            <div className="space-y-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="Search by name or email…"
                  className="pl-8"
                  autoFocus
                />
              </div>
              <div className="space-y-1 min-h-[120px] max-h-60 overflow-y-auto">
                {isFetching && <p className="text-sm text-gray-400 text-center py-4">Searching…</p>}
                {!isFetching && results.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    {debouncedQ ? 'No matching staff found.' : 'No other staff found for this client.'}
                  </p>
                )}
                {results.map((s: StaffSearchResultDto) => (
                  <div key={s.staffMemberId} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{s.firstName} {s.lastName}</p>
                      {s.email && <p className="text-xs text-gray-400">{s.email}</p>}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addExistingMutation.mutate(s.staffMemberId)}
                      disabled={addExistingMutation.isPending}
                    >
                      <Plus size={13} className="mr-1" />Add
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="wi-fn">First name *</Label>
                  <Input id="wi-fn" value={firstName} onChange={e => setFirstName(e.target.value)} autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="wi-ln">Last name *</Label>
                  <Input id="wi-ln" value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wi-email">Email (optional)</Label>
                <Input id="wi-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="For certificate delivery" />
              </div>
              <Button
                className="w-full"
                onClick={() => addNewMutation.mutate()}
                disabled={!firstName.trim() || !lastName.trim() || addNewMutation.isPending}
              >
                {addNewMutation.isPending ? 'Adding…' : 'Add walk-in'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function TrainerBookingRegisterPage() {
  const { formatDate, today } = useTrainerDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })
  }
  const { bookingId } = useParams<{ bookingId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showWalkIn, setShowWalkIn] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')

  const { data: register, isLoading } = useQuery({
    queryKey: ['trainer-register', bookingId],
    queryFn: () => trainerApi.getBookingRegister(bookingId!).then(r => r.data),
    enabled: !!bookingId,
    staleTime: 0,
    refetchInterval: 30_000,
  })

  const attendanceMutation = useMutation({
    mutationFn: ({ attendeeId, status, outcome }: { attendeeId: string; status: AttendanceStatus; outcome?: AttendanceOutcome }) =>
      trainerApi.updateAttendance(bookingId!, attendeeId, status, outcome),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] }),
  })

  const removeMutation = useMutation({
    mutationFn: (attendeeId: string) => trainerApi.removeWalkIn(bookingId!, attendeeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] }),
  })

  const completeMutation = useMutation({
    mutationFn: () => trainerApi.completeRegister(bookingId!),
    onSuccess: () => {
      setCompleting(false)
      queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['trainer-bookings'] })
      // Invalidate admin view so the booking detail reflects the completed status and attendance
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: () => trainerApi.rejectBooking(bookingId!, rejectionReason),
    onSuccess: () => {
      setRejecting(false)
      setRejectionReason('')
      queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['trainer-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['all-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] })
    },
  })

  const live = useLiveStatus({ bookingId: bookingId! }, !!bookingId)

  // Tabs: which one opens first depends on the day and whether a live session is running.
  const liveVisible = !!register && !!live.data?.enabled && !(register.isCancelled || register.isRejected)
  const liveRunning = liveVisible && live.data?.delivery?.status === 'Open'
  const openSession = !!register && !(register.isCancelled || register.isRejected || register.isCompleted)
  const defaultTab = !register || live.isLoading ? null
    : liveRunning ? 'live'
    : openSession && register.bookingDate <= today() ? 'attendance'
    : 'overview'
  const [tab, setTab] = usePageTab(['overview', 'attendance', ...(liveVisible ? ['live'] : []), 'materials'], defaultTab)

  if (isLoading || (!!register && live.isLoading)) return <p className="text-sm text-gray-400">Loading…</p>
  if (!register) return <p className="text-sm text-gray-500">Booking not found.</p>

  const venueAddress = [register.venueAddressLine1, register.venueCity, register.venuePostcode]
    .filter(Boolean).join(', ')

  const presentCount = register.attendees.filter(a => a.attendanceStatus === 'Present').length
  const absentCount = register.attendees.filter(a => a.attendanceStatus === 'Absent').length
  const notRecordedCount = register.attendees.filter(a => a.attendanceStatus === 'NotRecorded').length
  const isLocked = register.isCompleted || register.isCancelled || register.isRejected

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Back link */}
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
        <ChevronLeft size={15} /> Back
      </button>

      {/* Cancelled banner */}
      {register.isCancelled && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 flex items-center gap-3">
          <Ban size={18} className="text-red-400 shrink-0" />
          <div>
            <p className="font-semibold text-red-800">Booking cancelled</p>
            <p className="text-sm text-red-600">This booking has been cancelled.</p>
          </div>
        </div>
      )}

      {/* Rejected banner */}
      {register.isRejected && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">You rejected this booking</p>
            {register.rejectionReason && (
              <p className="text-sm text-amber-700 mt-0.5">Reason: "{register.rejectionReason}"</p>
            )}
            <p className="text-xs text-amber-600 mt-1">The admin has been notified and will reassign the booking.</p>
          </div>
        </div>
      )}

      {register.isCompleted && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4 text-center">
          <CheckCircle2 size={20} className="mx-auto text-green-500 mb-2" />
          <p className="text-sm font-medium text-green-800">Session marked as complete</p>
          <p className="text-xs text-green-600 mt-0.5">An admin will review and issue certificates.</p>
        </div>
      )}


      {/* What this page is about, whichever tab is open */}
      <div>
        <h1 className="text-lg font-semibold text-gray-900">
          {register.courseName}
          {register.courseCode && <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{register.courseCode}</span>}
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">{fmtDate(register.bookingDate)}</p>
      </div>

      <PageTabs
        label="Booking sections"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'attendance', label: 'Attendance', badge: register.attendees.length > 0 ? <TabCount>{presentCount} / {register.attendees.length}</TabCount> : undefined },
          ...(liveVisible ? [{ id: 'live', label: 'Live', badge: liveRunning ? <TabLiveDot label="A live session is running" /> : undefined }] : []),
          { id: 'materials', label: 'Materials' },
        ]}
      />

      <TabPanel id="overview" active={tab}>
        <Card>
          <CardContent className="space-y-4 text-sm pt-6">
            {/* Online delivery */}
            {register.deliveryMethod === 'Online' && (
              <div className="flex gap-3">
                <Globe size={15} className="shrink-0 text-indigo-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-800">Online</p>
                  {register.meetingUrl && (
                    <a href={register.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline break-all">
                      {register.meetingUrl}
                    </a>
                  )}
                  {register.meetingInstructions && (
                    <p className="text-gray-500 mt-1 whitespace-pre-line">{register.meetingInstructions}</p>
                  )}
                </div>
              </div>
            )}

            {/* Venue */}
            {register.deliveryMethod === 'InPerson' && register.venueName && (
              <div className="space-y-2">
                <div className="flex gap-3">
                  <MapPin size={15} className="shrink-0 text-indigo-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-800">{register.venueName}</p>
                    {venueAddress && (
                      <a
                        href={mapsUrl(venueAddress)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-500 hover:text-indigo-600 mt-0.5 inline-flex items-start gap-1 group"
                      >
                        <span>{venueAddress}</span>
                        <ExternalLink size={11} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                      </a>
                    )}
                    {register.venueNotes && (
                      <p className="text-gray-400 mt-1 text-xs italic">{register.venueNotes}</p>
                    )}
                  </div>
                </div>
                {venueAddress && (
                  <div className="rounded-lg overflow-hidden border border-gray-200 h-48">
                    <iframe
                      title="Venue location"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(venueAddress)}&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Client contact */}
            <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 space-y-1.5">
              <p className="font-medium text-gray-800">{register.clientName}</p>
              {register.clientContactName && <p className="text-gray-600 text-sm">{register.clientContactName}</p>}
              {register.clientContactEmail && (
                <a href={`mailto:${register.clientContactEmail}`} className="flex items-center gap-1.5 text-indigo-600 hover:underline text-xs">
                  <Mail size={12} />{register.clientContactEmail}
                </a>
              )}
              {register.clientPhone && (
                <a href={`tel:${register.clientPhone}`} className="flex items-center gap-1.5 text-indigo-600 hover:underline text-xs">
                  <Phone size={12} />{register.clientPhone}
                </a>
              )}
            </div>

            {/* Trainer team */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Your role</p>
              <p className="text-sm text-gray-700 font-medium">{register.myRole}</p>
              {register.coTrainers.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {register.coTrainers.map((t, i) => (
                    <p key={i} className="text-xs text-gray-500">
                      {t.firstName} {t.lastName}
                      <span className="ml-1 text-gray-400">· {t.role}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Booking notes */}
            {register.notes && (
              <p className="text-gray-500 italic">{register.notes}</p>
            )}

            <p className="text-gray-400">{register.places} place{register.places !== 1 ? 's' : ''} booked · {register.attendees.length} attendee{register.attendees.length !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>

        {!isLocked && (
          <div className="border-t pt-4">
            {rejecting ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 space-y-3">
                <p className="font-medium text-amber-800">Reject this booking?</p>
                <p className="text-sm text-amber-700">
                  The admin will be notified so they can reassign it to another trainer.
                </p>
                <textarea
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  placeholder="Reason for rejecting (required)…"
                  rows={3}
                  className="w-full rounded-md border border-amber-300 bg-white px-3 py-2 text-sm placeholder-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => rejectMutation.mutate()}
                    disabled={!rejectionReason.trim() || rejectMutation.isPending}
                  >
                    {rejectMutation.isPending ? 'Rejecting…' : 'Confirm rejection'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setRejecting(false); setRejectionReason('') }}>
                    Keep booking
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setRejecting(true)}
                className="text-amber-700 border-amber-200 hover:bg-amber-50 hover:border-amber-300"
              >
                <Ban size={14} className="mr-1.5" /> Reject booking
              </Button>
            )}
          </div>
        )}
      </TabPanel>

      <TabPanel id="attendance" active={tab}>
        <div className="mb-4">
          <SignInPanel
            target={{ bookingId: register.bookingId }}
            hidden={isLocked}
            onChanged={() => queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] })}
          />
        </div>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users size={15} className="text-indigo-500" />
                Attendance register
              </CardTitle>
              {!isLocked && (
                <Button size="sm" variant="outline" onClick={() => setShowWalkIn(true)}>
                  <UserPlus size={13} className="mr-1.5" /> Add walk-in
                </Button>
              )}
            </div>
            {/* Summary bar */}
            {register.attendees.length > 0 && (
              <div className="flex gap-4 text-xs mt-2">
                <span className="text-green-600 font-medium">{presentCount} present</span>
                <span className="text-red-500 font-medium">{absentCount} absent</span>
                <span className="text-gray-400">{notRecordedCount} not recorded</span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <IdentityCheckSummary
              required={register.photosEnabled && register.requirePhotoCheck}
              locked={isLocked}
              outstanding={register.attendees.filter(a => !a.identityCheckedAt).length}
            />
            {register.attendees.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No attendees yet. Add a walk-in to get started.</p>
            ) : (
              <div className="space-y-1.5">
                {register.attendees.map(a => (
                  <div key={a.attendeeId} className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2.5">
                    {/* Avatar: the attendee's photo when photos are on */}
                    <RegisterAvatar
                      source={{
                        key: `trainer-booking:${a.attendeeId}`,
                        name: `${a.firstName} ${a.lastName}`,
                        version: a.photoUpdatedAt,
                        enabled: register.photosEnabled,
                        canEdit: !isLocked,
                        notice: register.photoNotice,
                        load: () => trainerApi.getBookingAttendeePhoto(bookingId!, a.attendeeId).then(r => r.data),
                        upload: photo => trainerApi.setBookingAttendeePhoto(bookingId!, a.attendeeId, photo),
                        onChanged: () => queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] }),
                      }}
                      fallback={
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold shrink-0">
                          {a.firstName[0]?.toUpperCase()}{a.lastName[0]?.toUpperCase()}
                        </div>
                      }
                    />
                    {/* Name + email */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {a.firstName} {a.lastName}
                        {a.isWalkIn && (
                          <span className="ml-2 text-xs font-normal text-indigo-500 bg-indigo-50 rounded px-1.5 py-0.5">Walk-in</span>
                        )}
                      </p>
                      {a.email && <p className="text-xs text-gray-400 truncate">{a.email}</p>}
                      <IdentityCheck
                        enabled={register.photosEnabled}
                        required={register.requirePhotoCheck}
                        locked={isLocked}
                        hasPhoto={!!a.photoUpdatedAt}
                        checkedAt={a.identityCheckedAt}
                        checkedBy={a.identityCheckedBy}
                        onToggle={checked => trainerApi.setBookingIdentityCheck(bookingId!, a.attendeeId, checked)
                          .then(() => queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] }))}
                      />
                    </div>
                    {/* Pass/fail toggle — only meaningful once Present */}
                    {a.attendanceStatus === 'Present' && (
                      <OutcomeToggle
                        outcome={a.outcome ?? 'Passed'}
                        disabled={isLocked || attendanceMutation.isPending}
                        onClick={() => attendanceMutation.mutate({
                          attendeeId: a.attendeeId,
                          status: 'Present',
                          outcome: a.outcome === 'Failed' ? 'Passed' : 'Failed',
                        })}
                      />
                    )}
                    {/* Attendance toggle */}
                    <AttendanceBadge
                      status={a.attendanceStatus}
                      disabled={isLocked || attendanceMutation.isPending}
                      onClick={() => attendanceMutation.mutate({ attendeeId: a.attendeeId, status: nextStatus(a.attendanceStatus) })}
                    />
                    {/* Remove walk-in */}
                    {a.isWalkIn && !isLocked && (
                      <button
                        onClick={() => { if (confirm(`Remove ${a.firstName} ${a.lastName}?`)) removeMutation.mutate(a.attendeeId) }}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-300 hover:text-red-400 cursor-pointer shrink-0 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {!isLocked && (
          <div className="border-t pt-4 space-y-3">
            {completing ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 space-y-3">
                <p className="font-medium text-amber-800">
                  Mark session as complete?
                </p>
                <p className="text-sm text-amber-700">
                  {presentCount} attendee{presentCount !== 1 ? 's' : ''} marked present. An admin will issue certificates afterwards.
                </p>
                {notRecordedCount > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-100 px-3 py-2.5">
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800">
                      {notRecordedCount} attendee{notRecordedCount !== 1 ? 's' : ''} {notRecordedCount !== 1 ? 'haven\'t' : 'hasn\'t'} been marked yet — {notRecordedCount !== 1 ? 'their' : 'their'} attendance won't be recorded.
                    </p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => completeMutation.mutate()}
                    disabled={completeMutation.isPending}
                  >
                    {completeMutation.isPending ? 'Completing…' : 'Yes, complete session'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setCompleting(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setCompleting(true)}
                disabled={presentCount === 0}
                className="w-full"
              >
                {presentCount === 0 ? 'Mark at least one attendee present to complete' : `Mark session complete — ${presentCount} present`}
              </Button>
            )}
          </div>
        )}
      </TabPanel>

      {liveVisible && (
        <TabPanel id="live" active={tab}>
          <LiveRoomCard audience="trainer" target={{ bookingId: register.bookingId }} hidden={register.isCancelled || register.isRejected} />
        </TabPanel>
      )}

      <TabPanel id="materials" active={tab}>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen size={15} className="text-indigo-500" />
              Course materials
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CourseMaterialsSection courseId={register.courseId} />
          </CardContent>
        </Card>

      </TabPanel>

      {/* Walk-in modal */}
      {showWalkIn && (
        <WalkInModal
          bookingId={bookingId!}
          onClose={() => setShowWalkIn(false)}
          onAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['trainer-register', bookingId] })
            queryClient.invalidateQueries({ queryKey: ['booking', bookingId] })
            queryClient.invalidateQueries({ queryKey: ['staff'] })
          }}
        />
      )}
    </div>
  )
}
